import React, { useState, useEffect } from "react";
import { SuccessModal, ErrorModal } from "./messagesModal";
import { ChevronDown, X, Plus, MapPin, Building2 } from "lucide-react";
import { useSuperAdmin} from "../context/SuperAdminContext";

const BASE_URL = process.env.REACT_APP_BASE_URL;

// ── Reusable Location+Company Picker Section ──────────────────────────────────
const AttendanceLikeSection = ({
  label,
  requiredNote = "(at least one pair required *)",
  formCompany,
  pairs,
  onAdd,
  onRemove,
  locations,
  loadingLocations,
  selectedLocation,
  setSelectedLocation,
  setSelectedLocationObject,
  isLocationOpen,
  setIsLocationOpen,
  attendanceCompanies,
  loadingAttendanceCompanies,
  selectedCompanyId,
  setSelectedCompanyId,
  selectedCompanyName,
  setSelectedCompanyName,
  isCompanyOpen,
  setIsCompanyOpen,
}) => (
  <div className="mt-5 pt-4 border-t border-dashed border-indigo-300">
    <div className="flex items-center gap-2 mb-3">
      <MapPin size={16} className="text-indigo-600" />
      <span className="text-sm font-semibold text-indigo-700">{label}</span>
      <span className="text-xs text-red-500 font-medium">{requiredNote}</span>
    </div>

    {!formCompany ? (
      <div className="flex items-center gap-2 px-4 py-3 bg-amber-50 border border-amber-200 rounded-lg">
        <Building2 size={16} className="text-amber-500 flex-shrink-0" />
        <p className="text-sm text-amber-700 font-medium">
          Please select a company first to configure this access.
        </p>
      </div>
    ) : (
      <>
        {/* Picker row */}
        <div className="flex flex-col sm:flex-row gap-3 items-end">
          {/* Location dropdown */}
          <div className="flex-1 relative">
            <label className="block text-xs font-medium text-gray-600 mb-1">
              Location <span className="text-red-500">*</span>
            </label>
            <button
              type="button"
              onClick={() => setIsLocationOpen((p) => !p)}
              disabled={loadingLocations}
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg
                         flex justify-between items-center bg-white hover:border-indigo-400
                         focus:outline-none focus:ring-2 focus:ring-indigo-500 transition-all
                         disabled:bg-gray-100 disabled:cursor-not-allowed"
            >
              <span className={selectedLocation ? "text-gray-900" : "text-gray-400"}>
                {loadingLocations ? "Loading..." : selectedLocation || "Select Location"}
              </span>
              <ChevronDown
                className={`h-4 w-4 text-gray-500 transition-transform ${isLocationOpen ? "rotate-180" : ""}`}
              />
            </button>
            {isLocationOpen && !loadingLocations && (
              <div className="absolute z-50 left-0 right-0 mt-1 bg-white border border-gray-200
                              rounded-lg shadow-lg max-h-48 overflow-y-auto">
                {locations.length === 0 ? (
                  <div className="px-4 py-3 text-sm text-gray-500 text-center">No locations found</div>
                ) : (
                  locations.map((loc) => (
                    <button
                      key={loc._id || loc}
                      type="button"
                      onClick={() => {
                        setSelectedLocation(typeof loc === "string" ? loc : loc.name || "");
                        setSelectedLocationObject(loc);
                        setIsLocationOpen(false);
                      }}
                      className={`w-full text-left px-4 py-2 text-sm transition-colors
                        ${selectedLocation === (typeof loc === "string" ? loc : loc.name || "")
                          ? "bg-indigo-50 text-indigo-700 font-semibold"
                          : "hover:bg-gray-100"
                        }`}
                    >
                      {typeof loc === "string" ? loc : loc.name || String(loc._id) || ""}
                    </button>
                  ))
                )}
              </div>
            )}
          </div>

          {/* Company dropdown */}
          <div className="flex-1 relative">
            <label className="block text-xs font-medium text-gray-600 mb-1">
              Company <span className="text-red-500">*</span>
            </label>
            <button
              type="button"
              disabled={!selectedLocation || loadingAttendanceCompanies}
              onClick={() => selectedLocation && setIsCompanyOpen((p) => !p)}
              className={`w-full px-3 py-2 text-sm border rounded-lg
                flex justify-between items-center transition-all
                focus:outline-none focus:ring-2 focus:ring-indigo-500
                ${!selectedLocation || loadingAttendanceCompanies
                  ? "bg-gray-100 cursor-not-allowed text-gray-400 border-gray-200"
                  : "bg-white hover:border-indigo-400 border-gray-300"
                }`}
            >
              <span className={selectedCompanyName ? "text-gray-900" : "text-gray-400"}>
                {loadingAttendanceCompanies
                  ? "Loading..."
                  : selectedCompanyName || "Select Company"}
              </span>
              <ChevronDown
                className={`h-4 w-4 text-gray-500 transition-transform ${isCompanyOpen ? "rotate-180" : ""}`}
              />
            </button>
            {isCompanyOpen && !loadingAttendanceCompanies && (
              <div className="absolute z-50 left-0 right-0 mt-1 bg-white border border-gray-200
                              rounded-lg shadow-lg max-h-48 overflow-y-auto">
                {attendanceCompanies.length === 0 ? (
                  <div className="px-4 py-3 text-sm text-gray-500 text-center">No companies found</div>
                ) : (
                  attendanceCompanies.map((company) => (
                    <button
                      key={company._id}
                      type="button"
                      onClick={() => {
                        setSelectedCompanyId(company._id);
                        setSelectedCompanyName(company.name);
                        setIsCompanyOpen(false);
                      }}
                      className={`w-full text-left px-4 py-2 text-sm transition-colors
                        ${selectedCompanyId === company._id
                          ? "bg-indigo-50 text-indigo-700 font-semibold"
                          : "hover:bg-gray-100"
                        }`}
                    >
                      {company.name}
                    </button>
                  ))
                )}
              </div>
            )}
          </div>

          {/* Add button */}
          <button
            type="button"
            onClick={onAdd}
            disabled={!selectedLocation || !selectedCompanyId}
            className="flex items-center gap-1.5 px-4 py-2 text-sm font-semibold
                       bg-indigo-600 text-white rounded-lg hover:bg-indigo-700
                       disabled:opacity-40 disabled:cursor-not-allowed transition-colors
                       whitespace-nowrap"
          >
            <Plus size={16} />
            Add
          </button>
        </div>

        {/* Added pairs display */}
        {pairs.length > 0 && (
          <div className="mt-4">
            <p className="text-xs font-semibold text-gray-600 mb-2 uppercase tracking-wide">
              Selected Access ({pairs.length})
            </p>
            <div className="flex flex-wrap gap-2">
              {pairs.map((pair, index) => (
                <div
                  key={index}
                  className="flex items-center gap-2 pl-3 pr-2 py-1.5
                             bg-indigo-50 border border-indigo-200 rounded-full
                             text-xs text-indigo-800"
                >
                  <MapPin size={11} className="text-indigo-500 flex-shrink-0" />
                  <span className="font-medium">{pair.location}</span>
                  <span className="text-indigo-400">•</span>
                  <Building2 size={11} className="text-indigo-500 flex-shrink-0" />
                  <span>{pair.companyName}</span>
                  <button
                    type="button"
                    onClick={() => onRemove(index)}
                    className="ml-1 text-indigo-400 hover:text-red-500 transition-colors"
                  >
                    <X size={13} />
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}
      </>
    )}
  </div>
);

// ── Main Component ────────────────────────────────────────────────────────────
const UserManagement = () => {
  const { token } = useSuperAdmin();

  // State management
  const [showForm, setShowForm] = useState(false);
  const [companies, setCompanies] = useState([]);
  const [roles, setRoles] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [modalMessage, setModalMessage] = useState("");
  const [actionType, setActionType] = useState("");

  // Form state
  const [formData, setFormData] = useState({
    company: "",
    name: "",
    email: "",
    phone: "",
    Designation: "",
    type: "",
    status: "active",
    role: [],
  });

  // ── Mark Attendance section state ──────────────────────────────────────────
  const [locations, setLocations] = useState([]);
  const [loadingLocations, setLoadingLocations] = useState(false);
  const [attendanceCompanies, setAttendanceCompanies] = useState([]);
  const [loadingAttendanceCompanies, setLoadingAttendanceCompanies] = useState(false);

  const [selectedAttendanceLocation, setSelectedAttendanceLocation] = useState("");
  const [isLocationOpen, setIsLocationOpen] = useState(false);
  const [selectedAttendanceCompany, setSelectedAttendanceCompany] = useState("");
  const [selectedAttendanceCompanyName, setSelectedAttendanceCompanyName] = useState("");
  const [isAttendanceCompanyOpen, setIsAttendanceCompanyOpen] = useState(false);
  const [selectedattendancelocationobject, setselectedattendancelocationobject] = useState([]);

  const [attendancePairs, setAttendancePairs] = useState([]);
  // ──────────────────────────────────────────────────────────────────────────

  // ── Payout Generator section state ────────────────────────────────────────
  const [payoutGeneratorCompanies, setPayoutGeneratorCompanies] = useState([]);
  const [loadingPayoutGeneratorCompanies, setLoadingPayoutGeneratorCompanies] = useState(false);
  const [selectedPayoutGeneratorLocation, setSelectedPayoutGeneratorLocation] = useState("");
  const [selectedPayoutGeneratorLocationObject, setSelectedPayoutGeneratorLocationObject] = useState([]);
  const [isPayoutGeneratorLocationOpen, setIsPayoutGeneratorLocationOpen] = useState(false);
  const [selectedPayoutGeneratorCompany, setSelectedPayoutGeneratorCompany] = useState("");
  const [selectedPayoutGeneratorCompanyName, setSelectedPayoutGeneratorCompanyName] = useState("");
  const [isPayoutGeneratorCompanyOpen, setIsPayoutGeneratorCompanyOpen] = useState(false);
  const [payoutGeneratorPairs, setPayoutGeneratorPairs] = useState([]);
  // ──────────────────────────────────────────────────────────────────────────

  // ── Payout Accounts has no location/company picker (role flag only) ────────

  // ── Payout Approval section state ─────────────────────────────────────────
  const [payoutApprovalCompanies, setPayoutApprovalCompanies] = useState([]);
  const [loadingPayoutApprovalCompanies, setLoadingPayoutApprovalCompanies] = useState(false);
  const [selectedPayoutApprovalLocation, setSelectedPayoutApprovalLocation] = useState("");
  const [selectedPayoutApprovalLocationObject, setSelectedPayoutApprovalLocationObject] = useState([]);
  const [isPayoutApprovalLocationOpen, setIsPayoutApprovalLocationOpen] = useState(false);
  const [selectedPayoutApprovalCompany, setSelectedPayoutApprovalCompany] = useState("");
  const [selectedPayoutApprovalCompanyName, setSelectedPayoutApprovalCompanyName] = useState("");
  const [isPayoutApprovalCompanyOpen, setIsPayoutApprovalCompanyOpen] = useState(false);
  const [payoutApprovalPairs, setPayoutApprovalPairs] = useState([]);
  // ──────────────────────────────────────────────────────────────────────────

  // ── Manage Metadata section state ─────────────────────────────────────────
  const [manageMetadataCompanies, setManageMetadataCompanies] = useState([]);
  const [loadingManageMetadataCompanies, setLoadingManageMetadataCompanies] = useState(false);
  const [selectedManageMetadataLocation, setSelectedManageMetadataLocation] = useState("");
  const [selectedManageMetadataLocationObject, setSelectedManageMetadataLocationObject] = useState([]);
  const [isManageMetadataLocationOpen, setIsManageMetadataLocationOpen] = useState(false);
  const [selectedManageMetadataCompany, setSelectedManageMetadataCompany] = useState("");
  const [selectedManageMetadataCompanyName, setSelectedManageMetadataCompanyName] = useState("");
  const [isManageMetadataCompanyOpen, setIsManageMetadataCompanyOpen] = useState(false);
  const [manageMetadataPairs, setManageMetadataPairs] = useState([]);
  // ──────────────────────────────────────────────────────────────────────────

  const userTypes = ["superadmin", "admin", "flooradmin", "supervisor"];

  const isMarkAttendanceSelected = (() => {
    const markRole = roles.find((r) => r.name === "Mark Attendance");
    return markRole ? formData.role.includes(markRole.id) : false;
  })();

  const isPayoutGeneratorSelected = (() => {
    const r = roles.find((r) => r.name === "Payout Generator");
    return r ? formData.role.includes(r.id) : false;
  })();

  const isPayoutApprovalSelected = (() => {
    const r = roles.find((r) => r.name === "Payout Approver");
    return r ? formData.role.includes(r.id) : false;
  })();

  const isManageMetadataSelected = (() => {
    const r = roles.find((r) => r.name === "Manage Metadata");
    return r ? formData.role.includes(r.id) : false;
  })();

  // ── Computed role IDs ──────────────────────────────────────────────────────
  const markAttendanceRoleId = roles.find((r) => r.name === "Mark Attendance")?.id;
  const isMarkAttendanceChecked = markAttendanceRoleId
    ? formData.role.includes(markAttendanceRoleId)
    : false;

  // ── Effects ────────────────────────────────────────────────────────────────

  useEffect(() => {
    if (showForm) fetchCompanies();
  }, [showForm]);

  useEffect(() => {
    if (companies.length === 1 && companies[0]?._id) {
      fetchRolesByCompany(companies[0]._id);
      if (actionType === "modify") {
        fetchUsersForCompany(companies[0]._id);
      }
    }
  }, [companies, actionType]);

  // Fetch locations when Mark Attendance is selected
  useEffect(() => {
    if (isMarkAttendanceSelected && locations.length === 0) {
      fetchLocations();
    }
  }, [isMarkAttendanceSelected]);

  // Fetch locations when any Payout role or Manage Metadata is selected (reuse same locations list)
  useEffect(() => {
    if (
      (isPayoutGeneratorSelected || isPayoutApprovalSelected || isManageMetadataSelected) &&
      locations.length === 0
    ) {
      fetchLocations();
    }
  }, [isPayoutGeneratorSelected, isPayoutApprovalSelected, isManageMetadataSelected]);

  // Fetch attendance companies when Mark Attendance location changes
  useEffect(() => {
    if (selectedAttendanceLocation) {
      fetchCompaniesForSection(
        selectedattendancelocationobject,
        setAttendanceCompanies,
        setLoadingAttendanceCompanies
      );
      setSelectedAttendanceCompany("");
      setSelectedAttendanceCompanyName("");
    } else {
      setAttendanceCompanies([]);
    }
  }, [selectedAttendanceLocation]);

  // Fetch companies when Payout Generator location changes
  useEffect(() => {
    if (selectedPayoutGeneratorLocation) {
      fetchCompaniesForSection(
        selectedPayoutGeneratorLocationObject,
        setPayoutGeneratorCompanies,
        setLoadingPayoutGeneratorCompanies
      );
      setSelectedPayoutGeneratorCompany("");
      setSelectedPayoutGeneratorCompanyName("");
    } else {
      setPayoutGeneratorCompanies([]);
    }
  }, [selectedPayoutGeneratorLocation]);

  // Fetch companies when Payout Approval location changes
  useEffect(() => {
    if (selectedPayoutApprovalLocation) {
      fetchCompaniesForSection(
        selectedPayoutApprovalLocationObject,
        setPayoutApprovalCompanies,
        setLoadingPayoutApprovalCompanies
      );
      setSelectedPayoutApprovalCompany("");
      setSelectedPayoutApprovalCompanyName("");
    } else {
      setPayoutApprovalCompanies([]);
    }
  }, [selectedPayoutApprovalLocation]);

  // Fetch companies when Manage Metadata location changes
  useEffect(() => {
    if (selectedManageMetadataLocation) {
      fetchCompaniesForSection(
        selectedManageMetadataLocationObject,
        setManageMetadataCompanies,
        setLoadingManageMetadataCompanies
      );
      setSelectedManageMetadataCompany("");
      setSelectedManageMetadataCompanyName("");
    } else {
      setManageMetadataCompanies([]);
    }
  }, [selectedManageMetadataLocation]);

  // ── API calls ──────────────────────────────────────────────────────────────

  const fetchLocations = async () => {
    setLoadingLocations(true);
    try {
      const response = await fetch(`${BASE_URL}/lenskart/locations`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await response.json();
      if (data.success && Array.isArray(data.locations)) {
        setLocations(data.locations);
      }
    } catch (err) {
      console.error("Error fetching locations:", err);
    } finally {
      setLoadingLocations(false);
    }
  };

  // Generic company fetcher shared by all sections
  const fetchCompaniesForSection = async (locationObject, setCompaniesFn, setLoadingFn) => {
    setLoadingFn(true);
    try {
      const response = await fetch(
        `${BASE_URL}/onDemandCompanies/list-companies/${locationObject._id}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const data = await response.json();
      if (data && Array.isArray(data.companies)) {
        setCompaniesFn(data.companies);
      } else {
        setCompaniesFn([]);
      }
    } catch (err) {
      console.error("Error fetching companies:", err);
      setCompaniesFn([]);
    } finally {
      setLoadingFn(false);
    }
  };

  // Keep old name for backward compat (used in original effect)
  const fetchAttendanceCompanies = (locationObject) =>
    fetchCompaniesForSection(locationObject, setAttendanceCompanies, setLoadingAttendanceCompanies);

  const fetchCompanies = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${BASE_URL}/lenskart-admin/getCompanies`, {
        method: "GET",
        headers: { Authorization: `Bearer ${token}` },
      });
      const result = await response.json();
      if (result.success) {
        setCompanies(result.data.companies);
        if (result.data.companies.length === 1) {
          setFormData((prev) => ({
            ...prev,
            company: result.data.companies[0]._id,
          }));
        }
      } else {
        showError(result.message || "Error fetching companies");
      }
    } catch (error) {
      showError("Error fetching companies data");
    } finally {
      setLoading(false);
    }
  };

  const fetchRolesByCompany = async (companyId) => {
    const selectedCompany = companies.find((company) => company._id === companyId);
    const isUTV = selectedCompany && selectedCompany._id === "UTV";
    const payload = { UTVMember: isUTV };
    if (!isUTV && companyId) payload.MyCompany = companyId;

    try {
      const response = await fetch(`${BASE_URL}/lenskart-admin/getRolesByCompany`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });
      const result = await response.json();
      if (result.success) {
        setRoles(result.data.roles);
        return result.data.roles;
      } else {
        showError(result.message || "Error fetching roles");
        return [];
      }
    } catch (error) {
      showError("Error fetching roles data");
      return [];
    }
  };

  const fetchUsersForCompany = async (companyId) => {
    setLoadingUsers(true);
    try {
      const selectedCompany = companies.find((company) => company._id === companyId);
      const isUTV = selectedCompany && selectedCompany._id === "UTV";
      const payload = { UTVMember: isUTV };
      if (!isUTV && companyId) payload.MyCompany = companyId;

      const response = await fetch(`${BASE_URL}/lenskart-admin/getUsers`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify(payload),
      });
      const result = await response.json();
      if (result.success) {
        setUsers(result.data);
      } else {
        showError(result.message || "Error fetching users");
      }
    } catch (error) {
      showError("Error fetching users data");
    } finally {
      setLoadingUsers(false);
    }
  };

  const fetchUserDetails = async (userId) => {
    setLoading(true);
    try {
      const response = await fetch(`${BASE_URL}/lenskart-admin/getUser/${userId}`, {
        method: "GET",
        headers: { Authorization: `Bearer ${token}` },
      });
      const result = await response.json();
      if (result.success) {
        const userData = result.data;
        const userType =
          { super_admin: "superadmin", Admin: "admin", floor_admin: "flooradmin", Supervisor: "supervisor" }[userData.role] || "";
        const userCompany = userData.MyCompany || (userData.UTVMember ? "UTV" : "");

        let fetchedRoles = [];
        if (userCompany) fetchedRoles = await fetchRolesByCompany(userCompany);

        let userRoleIds = [];
        if (userData.featureAccess && Array.isArray(userData.featureAccess) && fetchedRoles.length > 0) {
          userRoleIds = userData.featureAccess
            .map((featureName) => {
              const matchingRole = fetchedRoles.find((role) => role.name === featureName);
              return matchingRole ? matchingRole.id : null;
            })
            .filter((id) => id !== null);
        }

        setFormData({
          _id: userData._id,
          company: userCompany,
          name: userData.name || "",
          email: userData.email || "",
          phone: userData.phone || "",
          Designation: userData.Designation || "",
          type: userType,
          status: userData.isActive ? "active" : "inactive",
          role: userRoleIds,
        });
        // ── Prefill Mark Attendance pairs ──────────────────────────────
        if (Array.isArray(userData.companies) && userData.companies.length > 0) {
          setAttendancePairs(
            userData.companies.map((item) => ({
              location: item.location?.name || "",
              locationId: item._id || "",
              companyId: item._id || "",
              companyName: item.name || "",
            }))
          );
        }

        // ── Prefill Payout Generator pairs ─────────────────────────────
        if (Array.isArray(userData.payoutGeneratorCompanies) && userData.payoutGeneratorCompanies.length > 0) {
          setPayoutGeneratorPairs(
            userData.payoutGeneratorCompanies.map((item) => ({
              location: item.location?.name || "",
              locationId: item._id || "",
              companyId: item._id || "",
              companyName: item.name || "",
            }))
          );
        }

        // ── Prefill Payout Approver pairs ──────────────────────────────
        if (Array.isArray(userData.payoutApproverCompanies) && userData.payoutApproverCompanies.length > 0) {
          setPayoutApprovalPairs(
            userData.payoutApproverCompanies.map((item) => ({
              location: item.location?.name || "",
              locationId: item._id || "",
              companyId: item._id || "",
              companyName: item.name || "",
            }))
          );
        }

        // ── Prefill Manage Metadata pairs ───────────────────────────────
        if (Array.isArray(userData.manageMetadataCompanies) && userData.manageMetadataCompanies.length > 0) {
          setManageMetadataPairs(
            userData.manageMetadataCompanies.map((item) => ({
              location: item.location?.name || "",
              locationId: item._id || "",
              companyId: item._id || "",
              companyName: item.name || "",
            }))
          );
        }
        setActionType("create");
      } else {
        showError(result.message || "Error fetching user details");
      }
    } catch (error) {
      showError("Error fetching user details");
    } finally {
      setLoading(false);
    }
  };

  // ── Helpers ────────────────────────────────────────────────────────────────

  const showError = (message) => { setModalMessage(message); setShowErrorModal(true); };
  const showSuccess = (message) => { setModalMessage(message); setShowSuccessModal(true); };

  const formatRoleName = (role) => {
    const roleMap = { super_admin: "SuperAdmin", floor_admin: "FloorAdmin", Admin: "Admin", Supervisor: "Supervisor" };
    return roleMap[role] || role || "No Type Assigned";
  };

  // ── Generic pair add/remove factory ───────────────────────────────────────

  const makeAddPair = (
    selectedLocation,
    selectedLocationObject,
    selectedCompanyId,
    selectedCompanyName,
    pairs,
    setPairs,
    resetLocation,
    resetCompany
  ) => () => {
    if (!selectedLocation || !selectedCompanyId) return;

    const alreadyExists = pairs.some(
      (p) => p.location === selectedLocation && p.companyId === selectedCompanyId
    );
    if (alreadyExists) return;

    const newPair = {
      location: selectedLocation,
      locationId: selectedLocationObject._id,
      companyId: selectedCompanyId,
      companyName: selectedCompanyName,
    };

    const updated = [...pairs, newPair];
    setPairs(updated);
    resetLocation();
    resetCompany();
  };

  const makeRemovePair = (pairs, setPairs) => (index) => {
    setPairs(pairs.filter((_, i) => i !== index));
  };

  // ── Mark Attendance handlers ───────────────────────────────────────────────

  const handleAddAttendancePair = makeAddPair(
    selectedAttendanceLocation,
    selectedattendancelocationobject,
    selectedAttendanceCompany,
    selectedAttendanceCompanyName,
    attendancePairs,
    setAttendancePairs,
    () => { setSelectedAttendanceLocation(""); setselectedattendancelocationobject([]); },
    () => { setSelectedAttendanceCompany(""); setSelectedAttendanceCompanyName(""); setAttendanceCompanies([]); }
  );

  const handleRemoveAttendancePair = makeRemovePair(attendancePairs, setAttendancePairs);

  // ── Payout Generator handlers ──────────────────────────────────────────────

  const handleAddPayoutGeneratorPair = makeAddPair(
    selectedPayoutGeneratorLocation,
    selectedPayoutGeneratorLocationObject,
    selectedPayoutGeneratorCompany,
    selectedPayoutGeneratorCompanyName,
    payoutGeneratorPairs,
    setPayoutGeneratorPairs,
    () => { setSelectedPayoutGeneratorLocation(""); setSelectedPayoutGeneratorLocationObject([]); },
    () => { setSelectedPayoutGeneratorCompany(""); setSelectedPayoutGeneratorCompanyName(""); setPayoutGeneratorCompanies([]); }
  );

  const handleRemovePayoutGeneratorPair = makeRemovePair(payoutGeneratorPairs, setPayoutGeneratorPairs);

  // ── Payout Approval handlers ───────────────────────────────────────────────

  const handleAddPayoutApprovalPair = makeAddPair(
    selectedPayoutApprovalLocation,
    selectedPayoutApprovalLocationObject,
    selectedPayoutApprovalCompany,
    selectedPayoutApprovalCompanyName,
    payoutApprovalPairs,
    setPayoutApprovalPairs,
    () => { setSelectedPayoutApprovalLocation(""); setSelectedPayoutApprovalLocationObject([]); },
    () => { setSelectedPayoutApprovalCompany(""); setSelectedPayoutApprovalCompanyName(""); setPayoutApprovalCompanies([]); }
  );

  const handleRemovePayoutApprovalPair = makeRemovePair(payoutApprovalPairs, setPayoutApprovalPairs);

  // ── Manage Metadata handlers ───────────────────────────────────────────────

  const handleAddManageMetadataPair = makeAddPair(
    selectedManageMetadataLocation,
    selectedManageMetadataLocationObject,
    selectedManageMetadataCompany,
    selectedManageMetadataCompanyName,
    manageMetadataPairs,
    setManageMetadataPairs,
    () => { setSelectedManageMetadataLocation(""); setSelectedManageMetadataLocationObject([]); },
    () => { setSelectedManageMetadataCompany(""); setSelectedManageMetadataCompanyName(""); setManageMetadataCompanies([]); }
  );

  const handleRemoveManageMetadataPair = makeRemovePair(manageMetadataPairs, setManageMetadataPairs);

  // ── Form handlers ──────────────────────────────────────────────────────────

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    if (name === "phone") {
      const digitsOnly = value.replace(/\D/g, "");
      if (digitsOnly === "" || /^[6-9]/.test(digitsOnly)) {
        if (digitsOnly.length <= 10) {
          setFormData((prev) => ({ ...prev, phone: digitsOnly }));
        }
      }
      return;
    }
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (name === "company" && value) {
      fetchRolesByCompany(value);
      if (actionType === "modify") fetchUsersForCompany(value);
    }
  };

  const handleRoleChange = (roleId) => {
    setFormData((prev) => {
      const currentRoles = prev.role || [];
      const newRoles = currentRoles.includes(roleId)
        ? currentRoles.filter((id) => id !== roleId)
        : [...currentRoles, roleId];

      // If unchecking Mark Attendance, clear its pairs
      const markRole = roles.find((r) => r.name === "Mark Attendance");
      if (markRole && roleId === markRole.id && currentRoles.includes(roleId)) {
        setAttendancePairs([]);
        setSelectedAttendanceLocation("");
        setSelectedAttendanceCompany("");
        setSelectedAttendanceCompanyName("");
      }

      // If unchecking Payout Generator, clear its pairs
      const payoutGenRole = roles.find((r) => r.name === "Payout Generator");
      if (payoutGenRole && roleId === payoutGenRole.id && currentRoles.includes(roleId)) {
        setPayoutGeneratorPairs([]);
        setSelectedPayoutGeneratorLocation("");
        setSelectedPayoutGeneratorCompany("");
        setSelectedPayoutGeneratorCompanyName("");
      }

      // If unchecking Payout Approver, clear its pairs
      const payoutAppRole = roles.find((r) => r.name === "Payout Approver");
      if (payoutAppRole && roleId === payoutAppRole.id && currentRoles.includes(roleId)) {
        setPayoutApprovalPairs([]);
        setSelectedPayoutApprovalLocation("");
        setSelectedPayoutApprovalCompany("");
        setSelectedPayoutApprovalCompanyName("");
      }

      // If unchecking Manage Metadata, clear its pairs
      const manageMetadataRole = roles.find((r) => r.name === "Manage Metadata");
      if (manageMetadataRole && roleId === manageMetadataRole.id && currentRoles.includes(roleId)) {
        setManageMetadataPairs([]);
        setSelectedManageMetadataLocation("");
        setSelectedManageMetadataCompany("");
        setSelectedManageMetadataCompanyName("");
      }

      return { ...prev, role: newRoles };
    });
  };

  const handleSelectAllRoles = () => {
    setFormData((prev) => ({
      ...prev,
      role: roles.length === prev.role.length ? [] : roles.map((role) => role.id),
    }));
  };

  const handleTypeChange = (e) => {
    const value = e.target.value;
    setFormData((prev) => ({ ...prev, type: value, role: value === "admin" ? prev.role : [] }));
  };

  // ── Validation ─────────────────────────────────────────────────────────────

  const validateForm = () => {
    if (!formData.company) { showError("Company selection is required"); return false; }
    if (!formData.name?.trim()) { showError("Name is required"); return false; }
    if (!formData.email) { showError("Email is required"); return false; }
    if (!formData.phone) { showError("Phone number is required"); return false; }
    if (formData.phone.length !== 10) { showError("Phone number must be exactly 10 digits"); return false; }
    if (!formData.type) { showError("User type is required"); return false; }
    if (formData.type === "admin" && formData.role.length === 0) {
      showError("At least one role is required for Admin type"); return false;
    }
    if (!/^[6-9]\d{9}$/.test(formData.phone)) {
      showError("Phone number must be 10 digits and start with 6, 7, 8, or 9");
      return false;
    }
    if (isMarkAttendanceChecked && attendancePairs.length === 0) {
      showError("Please select at least one Location & Company pair for Mark Attendance access"); return false;
    }
    if (isPayoutGeneratorSelected && payoutGeneratorPairs.length === 0) {
      showError("Please select at least one Location & Company pair for Payout Generator access"); return false;
    }
    if (isPayoutApprovalSelected && payoutApprovalPairs.length === 0) {
      showError("Please select at least one Location & Company pair for Payout Approver access"); return false;
    }
    if (isManageMetadataSelected && manageMetadataPairs.length === 0) {
      showError("Please select at least one Location & Company pair for Manage Metadata access"); return false;
    }
    return true;
  };

  // ── Submit ─────────────────────────────────────────────────────────────────

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;
    setLoading(true);

    const selectedFeatureAccess = formData.role.map((roleId) => {
      const roleData = roles.find((r) => r.id === roleId);
      return roleData ? roleData.name : "";
    });

    const selectedCompany = companies.find((company) => company._id === formData.company);
    const isUdyogTechVenture = selectedCompany && selectedCompany._id === "UTV";

    const payload = {
      phone: formData.phone,
      name: formData.name,
      email: formData.email,
      role:
        formData.type === "superadmin" ? "super_admin"
        : formData.type === "admin" ? "Admin"
        : formData.type === "supervisor" ? "Supervisor"
        : formData.type === "flooradmin" ? "floor_admin"
        : "",
      isActive: formData.status === "active",
      featureAccess: selectedFeatureAccess,
      UTVMember: isUdyogTechVenture,
      ...(attendancePairs.length > 0 && {
        markAttendanceAccess: attendancePairs.map(({ location, locationId, companyId, companyName }) => ({
          location, locationId, companyId, companyName,
        })),
      }),
      ...(payoutGeneratorPairs.length > 0 && {
        payoutGeneratorAccess: payoutGeneratorPairs.map(({ location, locationId, companyId, companyName }) => ({
          location, locationId, companyId, companyName,
        })),
      }),
      ...(payoutApprovalPairs.length > 0 && {
        payoutApproverAccess: payoutApprovalPairs.map(({ location, locationId, companyId, companyName }) => ({
          location, locationId, companyId, companyName,
        })),
      }),
      ...(manageMetadataPairs.length > 0 && {
        manageMetadataAccess: manageMetadataPairs.map(({ location, locationId, companyId, companyName }) => ({
          location, locationId, companyId, companyName,
        })),
      }),
    };

    if (formData.Designation?.trim()) payload.Designation = formData.Designation;
    if (!isUdyogTechVenture && formData.company) payload.MyCompany = formData.company;

    console.log("Submit payload:", payload);
    console.log("Mark Attendance pairs:", attendancePairs);
    console.log("Payout Generator pairs:", payoutGeneratorPairs);
    console.log("Payout Approval pairs:", payoutApprovalPairs);
    console.log("Manage Metadata pairs:", manageMetadataPairs);

    try {
      const isUpdate = formData._id;
      const method = isUpdate ? "PUT" : "POST";
      const url = `${BASE_URL}/lenskart-admin/createUser${isUpdate ? `/${formData._id}` : ""}`;

      const response = await fetch(url, {
        method,
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify(payload),
      });
      const result = await response.json();

      if (!response.ok) throw new Error(`Failed to ${isUpdate ? "update" : "create"} user`);

      if (response.status === 200 && !result.data) {
        setModalMessage(result.message);
        setShowErrorModal(true);
        closeForm();
        return;
      }

      setModalMessage(`User ${isUpdate ? "updated" : "created"} successfully!`);
      setShowSuccessModal(true);
      closeForm();
    } catch (error) {
      setModalMessage(error.message);
      setShowErrorModal(true);
    } finally {
      setLoading(false);
    }
  };

  // ── Form management ────────────────────────────────────────────────────────

  const resetForm = () => {
    setFormData({ company: "", name: "", email: "", phone: "", Designation: "", type: "", status: "active", role: [] });
    setRoles([]);
    setUsers([]);
    // Mark Attendance
    setAttendancePairs([]);
    setSelectedAttendanceLocation("");
    setSelectedAttendanceCompany("");
    setSelectedAttendanceCompanyName("");
    setAttendanceCompanies([]);
    // Payout Generator
    setPayoutGeneratorPairs([]);
    setSelectedPayoutGeneratorLocation("");
    setSelectedPayoutGeneratorCompany("");
    setSelectedPayoutGeneratorCompanyName("");
    setPayoutGeneratorCompanies([]);
    // Payout Approval
    setPayoutApprovalPairs([]);
    setSelectedPayoutApprovalLocation("");
    setSelectedPayoutApprovalCompany("");
    setSelectedPayoutApprovalCompanyName("");
    setPayoutApprovalCompanies([]);
    // Manage Metadata
    setManageMetadataPairs([]);
    setSelectedManageMetadataLocation("");
    setSelectedManageMetadataCompany("");
    setSelectedManageMetadataCompanyName("");
    setManageMetadataCompanies([]);
  };

  const openCreateForm = () => { setShowForm(true); setActionType("create"); resetForm(); };
  const openModifyForm = () => { setShowForm(true); setActionType("modify"); resetForm(); };

  const closeForm = () => {
    const wasModifyingUser = formData._id && actionType === "create";
    setShowForm(wasModifyingUser);
    setActionType(wasModifyingUser ? "modify" : "create");
    resetForm();
    if (wasModifyingUser && formData.company) fetchUsersForCompany(formData.company);
  };

  const isRoleSelectionEnabled = formData.type === "admin";

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 py-8 px-4 pt-24">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold text-gray-900 mb-2 bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
            User Management
          </h1>
          <p className="text-lg text-gray-600">
            Efficiently manage user accounts, permissions, and access levels
          </p>
        </div>

        {/* Action Buttons */}
        {!showForm && (
          <div className="flex justify-center gap-6 mb-8">
            <button
              onClick={openCreateForm}
              className="bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 px-8 rounded-lg transition-colors"
            >
              Add New User
            </button>
            <button
              onClick={openModifyForm}
              className="bg-green-600 hover:bg-green-700 text-white font-semibold py-3 px-8 rounded-lg transition-colors"
            >
              Modify Existing User
            </button>
          </div>
        )}

        {/* Form Section */}
        {showForm && (
          <div className="bg-white rounded-xl shadow-lg p-6 border border-gray-200">
            <div className="flex justify-between items-center mb-6 pb-4 border-b border-gray-200">
              <div>
                <h2 className="text-2xl font-bold text-gray-900">
                  {formData._id ? "Modify User" : actionType === "create" ? "Create New User" : "Modify Existing Users"}
                </h2>
                <p className="text-gray-600">
                  {formData._id
                    ? "Edit user details below"
                    : actionType === "create"
                    ? "Fill in the user details below"
                    : "Select a company to modify its users"}
                </p>
              </div>
              <button onClick={closeForm} className="p-2 hover:bg-gray-100 rounded-lg">
                <svg className="w-6 h-6 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Company Dropdown */}
            {companies.length > 1 ? (
              <div className="mb-6">
                <label className="block text-sm font-semibold text-gray-800 mb-2">Company *</label>
                <select
                  name="company"
                  value={formData.company}
                  onChange={handleInputChange}
                  required
                  disabled={loading}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="">Select Company</option>
                  {companies.map((company) => (
                    <option key={company._id} value={company._id}>
                      {company._id === "UTV" ? "Udyog Tech Venture" : `${company.name} - ${company.location}`}
                    </option>
                  ))}
                </select>
              </div>
            ) : companies.length === 1 && (
              <div className="mb-6">
                <label className="block text-sm font-semibold text-gray-800 mb-2">Company</label>
                <select
                  name="company"
                  value={formData.company}
                  disabled
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-50 text-gray-700"
                >
                  <option value={companies[0]._id}>
                    {companies[0]._id === "UTV" ? "Udyog Tech Venture" : `${companies[0].name} - ${companies[0].location}`}
                  </option>
                </select>
              </div>
            )}

            {/* Create Form Fields */}
            {actionType === "create" && (
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-4">
                  <div>
                    <label className="block text-sm font-semibold text-gray-800 mb-2">Name *</label>
                    <input
                      type="text" name="name" value={formData.name} onChange={handleInputChange} required
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="Enter full name"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-800 mb-2">Email ID *</label>
                    <input
                      type="email" name="email" value={formData.email} onChange={handleInputChange} required
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="Enter email address"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-800 mb-2">Phone No *</label>
                    <input
                      type="tel" name="phone" value={formData.phone} onChange={handleInputChange} required maxLength="10"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="Enter 10-digit phone number"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-800 mb-2">Designation</label>
                    <input
                      type="text" name="Designation" value={formData.Designation} onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="Enter Designation (optional)"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-800 mb-2">User Type *</label>
                    <select
                      name="type" value={formData.type} onChange={handleTypeChange} required
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="">Select User Type</option>
                      {userTypes.map((type) => (
                        <option key={type} value={type}>{type.charAt(0).toUpperCase() + type.slice(1)}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-800 mb-2">Status *</label>
                    <div className="flex gap-4 mt-3">
                      <label className="flex items-center gap-2">
                        <input type="radio" name="status" value="active" checked={formData.status === "active"} onChange={handleInputChange} className="text-blue-600" />
                        Active
                      </label>
                      <label className="flex items-center gap-2">
                        <input type="radio" name="status" value="inactive" checked={formData.status === "inactive"} onChange={handleInputChange} className="text-blue-600" />
                        Inactive
                      </label>
                    </div>
                  </div>
                </div>

                {/* ── Roles Section ── */}
                {isRoleSelectionEnabled && (
                  <div className="pt-4 border-t border-gray-200">
                    <label className="block text-sm font-semibold text-gray-800 mb-2">Roles *</label>
                    <div className="border border-gray-300 rounded-lg p-4">
                      {/* Select All */}
                      <label className="flex items-center gap-2 mb-3">
                        <input
                          type="checkbox"
                          checked={formData.role.length === roles.length && roles.length > 0}
                          onChange={handleSelectAllRoles}
                          className="text-blue-600"
                        />
                        <span className="font-medium">Select All</span>
                      </label>

                      {/* Role checkboxes grid */}
                      <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                        {roles.map((role) => (
                          <label key={role.id} className="flex items-center gap-2">
                            <input
                              type="checkbox"
                              checked={formData.role.includes(role.id)}
                              onChange={() => handleRoleChange(role.id)}
                              className="text-blue-600"
                            />
                            {role.name}
                          </label>
                        ))}
                      </div>

                      {/* ── Mark Attendance section ── */}
                      {isMarkAttendanceChecked && (
                        <AttendanceLikeSection
                          label="Mark Attendance Access — Select Location & Company"
                          formCompany={formData.company}
                          pairs={attendancePairs}
                          onAdd={handleAddAttendancePair}
                          onRemove={handleRemoveAttendancePair}
                          locations={locations}
                          loadingLocations={loadingLocations}
                          selectedLocation={selectedAttendanceLocation}
                          setSelectedLocation={setSelectedAttendanceLocation}
                          setSelectedLocationObject={setselectedattendancelocationobject}
                          isLocationOpen={isLocationOpen}
                          setIsLocationOpen={setIsLocationOpen}
                          attendanceCompanies={attendanceCompanies}
                          loadingAttendanceCompanies={loadingAttendanceCompanies}
                          selectedCompanyId={selectedAttendanceCompany}
                          setSelectedCompanyId={setSelectedAttendanceCompany}
                          selectedCompanyName={selectedAttendanceCompanyName}
                          setSelectedCompanyName={setSelectedAttendanceCompanyName}
                          isCompanyOpen={isAttendanceCompanyOpen}
                          setIsCompanyOpen={setIsAttendanceCompanyOpen}
                        />
                      )}

                      {/* ── Payout Generator section ── */}
                      {isPayoutGeneratorSelected && (
                        <AttendanceLikeSection
                          label="Payout Generator Access — Select Location & Company"
                          formCompany={formData.company}
                          pairs={payoutGeneratorPairs}
                          onAdd={handleAddPayoutGeneratorPair}
                          onRemove={handleRemovePayoutGeneratorPair}
                          locations={locations}
                          loadingLocations={loadingLocations}
                          selectedLocation={selectedPayoutGeneratorLocation}
                          setSelectedLocation={setSelectedPayoutGeneratorLocation}
                          setSelectedLocationObject={setSelectedPayoutGeneratorLocationObject}
                          isLocationOpen={isPayoutGeneratorLocationOpen}
                          setIsLocationOpen={setIsPayoutGeneratorLocationOpen}
                          attendanceCompanies={payoutGeneratorCompanies}
                          loadingAttendanceCompanies={loadingPayoutGeneratorCompanies}
                          selectedCompanyId={selectedPayoutGeneratorCompany}
                          setSelectedCompanyId={setSelectedPayoutGeneratorCompany}
                          selectedCompanyName={selectedPayoutGeneratorCompanyName}
                          setSelectedCompanyName={setSelectedPayoutGeneratorCompanyName}
                          isCompanyOpen={isPayoutGeneratorCompanyOpen}
                          setIsCompanyOpen={setIsPayoutGeneratorCompanyOpen}
                        />
                      )}


                      {/* ── Payout Approver section ── */}
                      {isPayoutApprovalSelected && (
                        <AttendanceLikeSection
                          label="Payout Approver Access — Select Location & Company"
                          formCompany={formData.company}
                          pairs={payoutApprovalPairs}
                          onAdd={handleAddPayoutApprovalPair}
                          onRemove={handleRemovePayoutApprovalPair}
                          locations={locations}
                          loadingLocations={loadingLocations}
                          selectedLocation={selectedPayoutApprovalLocation}
                          setSelectedLocation={setSelectedPayoutApprovalLocation}
                          setSelectedLocationObject={setSelectedPayoutApprovalLocationObject}
                          isLocationOpen={isPayoutApprovalLocationOpen}
                          setIsLocationOpen={setIsPayoutApprovalLocationOpen}
                          attendanceCompanies={payoutApprovalCompanies}
                          loadingAttendanceCompanies={loadingPayoutApprovalCompanies}
                          selectedCompanyId={selectedPayoutApprovalCompany}
                          setSelectedCompanyId={setSelectedPayoutApprovalCompany}
                          selectedCompanyName={selectedPayoutApprovalCompanyName}
                          setSelectedCompanyName={setSelectedPayoutApprovalCompanyName}
                          isCompanyOpen={isPayoutApprovalCompanyOpen}
                          setIsCompanyOpen={setIsPayoutApprovalCompanyOpen}
                        />
                      )}

                      {/* ── Manage Metadata section ── */}
                      {isManageMetadataSelected && (
                        <AttendanceLikeSection
                          label="Manage Metadata Access — Select Location & Company"
                          formCompany={formData.company}
                          pairs={manageMetadataPairs}
                          onAdd={handleAddManageMetadataPair}
                          onRemove={handleRemoveManageMetadataPair}
                          locations={locations}
                          loadingLocations={loadingLocations}
                          selectedLocation={selectedManageMetadataLocation}
                          setSelectedLocation={setSelectedManageMetadataLocation}
                          setSelectedLocationObject={setSelectedManageMetadataLocationObject}
                          isLocationOpen={isManageMetadataLocationOpen}
                          setIsLocationOpen={setIsManageMetadataLocationOpen}
                          attendanceCompanies={manageMetadataCompanies}
                          loadingAttendanceCompanies={loadingManageMetadataCompanies}
                          selectedCompanyId={selectedManageMetadataCompany}
                          setSelectedCompanyId={setSelectedManageMetadataCompany}
                          selectedCompanyName={selectedManageMetadataCompanyName}
                          setSelectedCompanyName={setSelectedManageMetadataCompanyName}
                          isCompanyOpen={isManageMetadataCompanyOpen}
                          setIsCompanyOpen={setIsManageMetadataCompanyOpen}
                        />
                      )}
                    </div>
                  </div>
                )}

                {/* Form Actions */}
                <div className="flex gap-4 pt-4 border-t border-gray-200">
                  <button
                    type="submit"
                    disabled={loading}
                    className="flex-1 bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 px-6 rounded-lg disabled:bg-blue-400"
                  >
                    {loading ? "Processing..." : formData._id ? "Update User" : "Create User"}
                  </button>
                  <button
                    type="button" onClick={closeForm}
                    className="flex-1 bg-gray-500 hover:bg-gray-600 text-white font-semibold py-3 px-6 rounded-lg"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            )}

            {/* Modify Mode - User List */}
            {actionType === "modify" && (
              <div>
                {loadingUsers ? (
                  <div className="text-center py-8">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
                    <p className="mt-2 text-gray-600">Loading users...</p>
                  </div>
                ) : users.length > 0 ? (
                  <div>
                    <h3 className="text-lg font-semibold text-gray-800 mb-4">Users ({users.length})</h3>
                    <div className="space-y-3 max-h-96 overflow-y-auto">
                      {users.map((user) => (
                        <div
                          key={user._id}
                          className="border border-gray-200 rounded-lg p-4 hover:bg-gray-50 cursor-pointer"
                          onClick={() => fetchUserDetails(user._id)}
                        >
                          <div className="flex justify-between items-start">
                            <div>
                              <span className="bg-blue-100 text-blue-800 text-sm font-medium px-2 py-1 rounded">
                                {formatRoleName(user.role)}
                              </span>
                              <p className="font-medium text-gray-900 mt-2">{user.name}</p>
                              <p className="text-sm text-gray-600">{user.email}</p>
                              <p className="text-sm text-gray-600">{user.phone}</p>
                              {user.Designation && (
                                <p className="text-sm text-gray-500">Designation: {user.Designation}</p>
                              )}
                            </div>
                            <span
                              className={`px-2 py-1 rounded-full text-xs font-medium ${
                                user.isActive ? "bg-green-100 text-green-800" : "bg-red-100 text-red-800"
                              }`}
                            >
                              {user.isActive ? "Active" : "Inactive"}
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ) : formData.company ? (
                  <div className="text-center py-8">
                    <p className="text-gray-600">No users found for this company</p>
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <p className="text-gray-600">Select a company to view users</p>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Modals */}
      <SuccessModal isOpen={showSuccessModal} onClose={() => setShowSuccessModal(false)} message={modalMessage} />
      <ErrorModal isOpen={showErrorModal} onClose={() => setShowErrorModal(false)} message={modalMessage} />
    </div>
  );
};

export default UserManagement;